import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "./PageShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupportPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [state, setState] = useState("idle"); // idle | loading | sent

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    setState("loading");
    try {
      await axios.post(`${API}/support`, form);
      setState("sent");
    } catch (err) {
      setState("idle");
      toast.error("Something went wrong. Please try again.");
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-[rgba(26,24,23,0.14)] bg-white px-5 py-4 text-sm text-ink placeholder:text-[#8A8079] outline-none transition-colors duration-300 focus:border-gold";

  return (
    <PageShell eyebrow="We're here to help" title="Support">
      <p className="max-w-xl text-sm leading-relaxed text-[#4A433F] sm:text-base">
        A question about 841, the waitlist, or membership? Send us a message below — or write to us
        directly at <strong>support@the841.com</strong> — and we'll get back to you as soon as we
        can.
      </p>

      {state === "sent" ? (
        <motion.div
          data-testid="support-success-message"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="flex max-w-xl items-center gap-4 rounded-3xl border border-[rgba(195,153,83,0.3)] bg-white/80 px-6 py-5 backdrop-blur-xl"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold-dark">
            <Check size={18} />
          </span>
          <div>
            <p className="font-serif text-xl text-ink">Message received.</p>
            <p className="text-sm text-[#5C5450]">Thank you — we'll be in touch soon.</p>
          </div>
        </motion.div>
      ) : (
        <form data-testid="support-form" onSubmit={submit} className="max-w-xl space-y-4">
          <input
            data-testid="support-name-input"
            type="text"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Your name"
            className={inputCls}
          />
          <input
            data-testid="support-email-input"
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="Your email address"
            className={inputCls}
          />
          <textarea
            data-testid="support-message-input"
            required
            rows={5}
            value={form.message}
            onChange={set("message")}
            placeholder="How can we help?"
            className={`${inputCls} resize-none`}
          />
          <button
            data-testid="support-submit-button"
            type="submit"
            disabled={state === "loading"}
            className="rounded-full bg-ink px-8 py-4 text-sm tracking-wide text-sand transition-colors duration-300 hover:bg-gold-dark disabled:opacity-60"
          >
            {state === "loading" ? "Sending…" : "Send message"}
          </button>
        </form>
      )}
    </PageShell>
  );
}
